import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Table,
  Space,
  message,
  Spin,
  Alert,
  Popconfirm,
  Row,
  Col,
  Typography,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { EditOutlined, CopyOutlined, PrinterOutlined, RetweetOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useWorkOrders,
  useWorkOrder,
} from '../../hooks/useWorkOrder';
import { useSalesOrder } from '../../hooks/useSalesOrder';
import {
  useWorkOrderStages,
  useStageDimensions,
  useAddStageDimension,
  useStageInspectionByStage,
  useStageInspection,
  useStageInspectionMeasurements,
  useCreateStageInspection,
  useStartInspection,
  useRecordMeasurements,
  useUpdateReportMetadata,
  useUsers,
} from '../../hooks/useQCWorkflow';
import {
  sciService,
  workOrderStageService,
  WorkOrderStage,
  WorkOrderStageDimension,
  StageInspectionMeasurement,
  StageCompletionInspection,
} from '../../services/qc-workflow.service';
import { calibrationService } from '../../services/calibration.service';
import { tmmdeStatusLabel } from '../tmmde/tmmdeDisplay';
import { useTmmdeUseOverride } from '../../hooks/useTmmdeUseOverride';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { EngineeringSymbolPalette } from '../../components/quality/EngineeringSymbolPalette';
import settingsService from '../../services/settingsService';
import './DimensionalInspectionPage.css';

const { TextArea, Search } = Input;
const { Title } = Typography;
const EMPTY_ARRAY: any[] = [];

export interface ReportMetadata {
  report_no?: string;
  job_card_no?: string;
  drawing_reference?: string;
  revision?: string;
  client?: string;
  part_no_description?: string;
  heat_number?: string;
  customer_job_no?: string;
  material?: string;
  inspected_by?: string;
  date_of_inspection?: string;
  qty?: number;
  po_line_no?: string;
  client_po_no?: string;
  /** Display labels (used on print); the first entries correspond to instrument_asset_ids, then legacy free-text identifiers. */
  instruments_used?: string[];
  instrument_asset_ids?: string[];
  remarks?: string;
  qc_approved_by?: string;
  qc_approval_date?: string;
}

interface DimensionTableRow {
  key: string;
  id?: string;
  dimension_name: string;
  requirement_text: string;
  sequence_number: number;
  isPersisted: boolean;
}

/** Tablet-friendly: min touch target 44px, responsive layout */
const TOUCH_MIN_HEIGHT = 44;
const DEFAULT_DIMENSION_ROW_COUNT = 5;

/** Form value for the instrument picker: equipment asset ids followed by any legacy text identifiers. */
function instrumentFormValue(meta: ReportMetadata): string[] | undefined {
  const ids = meta.instrument_asset_ids;
  if (!Array.isArray(ids)) return meta.instruments_used;
  return [...ids, ...(meta.instruments_used ?? []).slice(ids.length)];
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function instrumentMetadata(
  selected: string[] | undefined,
  printLabelById: Record<string, string>,
  previous: ReportMetadata | undefined
): Pick<ReportMetadata, 'instruments_used' | 'instrument_asset_ids'> {
  const values = (selected ?? []).filter(Boolean);
  const isAssetId = (v: string) => printLabelById[v] !== undefined || UUID_PATTERN.test(v);
  const previousLabel: Record<string, string> = {};
  (previous?.instrument_asset_ids ?? []).forEach((id, i) => {
    const label = previous?.instruments_used?.[i];
    if (label) previousLabel[id] = label;
  });
  const ids = values.filter(isAssetId);
  const legacy = values.filter((v) => !isAssetId(v));
  return {
    instrument_asset_ids: ids,
    instruments_used: [...ids.map((id) => printLabelById[id] ?? previousLabel[id] ?? id), ...legacy],
  };
}

function makeDraftRow(sequenceNumber: number): DimensionTableRow {
  return {
    key: `draft-${sequenceNumber}-${Math.random().toString(36).slice(2, 8)}`,
    dimension_name: '',
    requirement_text: '',
    sequence_number: sequenceNumber,
    isPersisted: false,
  };
}

function getUserDisplayName(userLike: any): string {
  const fullName = `${String(userLike?.first_name ?? '').trim()} ${String(userLike?.last_name ?? '').trim()}`.trim();
  return fullName || String(userLike?.name ?? '').trim() || String(userLike?.username ?? '').trim() || String(userLike?.email ?? '').trim();
}

/** Parse dimension numeric fields from text inputs (engineering keypad may insert +, Unicode −, etc.). */
function parseOptionalDimensionNumber(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  const s = String(value)
    .trim()
    .replace(/\u2212/g, '-')
    .replace(/,/g, '')
    .replace(/\s+/g, '');
  if (s === '' || s === '+' || s === '-') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function formatMeasurementDisplayValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const normalized = raw.replace(/(\.\d*?[1-9])0+$|\.0+$/u, '$1');
  return normalized;
}

function openDimensionalInspectionPrint(id: string): void {
  window.location.assign(`/print/qms/dimensional-inspections/${id}`);
}

function incrementRevisionValue(value: unknown): string {
  const text = String(value ?? '').trim();
  if (!text) return '1';

  const numericMatch = text.match(/^(.*?)(\d+)(\D*)$/);
  if (numericMatch) {
    const [, prefix, digits, suffix] = numericMatch;
    return `${prefix}${String(Number(digits) + 1).padStart(digits.length, '0')}${suffix}`;
  }

  const alphaMatch = text.match(/^(.*?)([A-Za-z])$/);
  if (alphaMatch) {
    const [, prefix, letter] = alphaMatch;
    const code = letter.charCodeAt(0);
    if (code >= 65 && code < 90) return `${prefix}${String.fromCharCode(code + 1)}`;
    if (code >= 97 && code < 122) return `${prefix}${String.fromCharCode(code + 1)}`;
  }

  return `${text} 1`;
}

type DraftPrefillState = {
  sourceInspectionId?: string;
  prefillMode?: 'clone' | 'revision';
};

type DimensionalInspectionPageProps = {
  mode?: 'list' | 'form';
};

const DimensionalInspectionPage: React.FC<DimensionalInspectionPageProps> = ({ mode = 'list' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeInspectionId } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [inspectionId, setInspectionId] = useState<string>(routeInspectionId || '');
  const [searchText, setSearchText] = useState('');
  const [workOrderId, setWorkOrderId] = useState<string>('');
  const [stageId, setStageId] = useState<string>('');
  const [reportForm] = Form.useForm();
  /** Inline add-dimension form (same page as grid — no modal). */
  const [dimensionRows, setDimensionRows] = useState<DimensionTableRow[]>([]);
  const [visibleRowCount, setVisibleRowCount] = useState<number>(DEFAULT_DIMENSION_ROW_COUNT);
  const [measurementValues, setMeasurementValues] = useState<Record<string, string>>({});
  const [isGeneratingReportNo, setIsGeneratingReportNo] = useState(false);
  const lastReportMetadataSyncRef = useRef<string>('');
  const lastDefaultFillRef = useRef<string>('');
  const lastCreateResetRef = useRef<string>('');
  const lastWorkOrderResetRef = useRef<string>('');
  const measurementCellRef = useRef<{ key: string; input: HTMLInputElement | null }>({
    key: '',
    input: null,
  });
  /** Last-focused text control in add-dimension form (caret stored because symbol buttons steal focus). */
  const dimensionTextFieldRef = useRef<{
    rowKey: string;
    field: 'dimension_name' | 'requirement_text';
    input: HTMLInputElement | HTMLTextAreaElement;
    selStart: number;
    selEnd: number;
  } | null>(null);
  const isStandaloneForm = mode === 'form';
  const isCreatePage = mode === 'form' && !routeInspectionId;
  const formPageTitle = isCreatePage ? 'Create Dimensional Inspection' : 'Edit Dimensional Inspection';
  const draftState = (location.state || {}) as DraftPrefillState;
  const prefillMode = isCreatePage ? draftState.prefillMode : undefined;
  const prefillSourceId = isCreatePage ? String(draftState.sourceInspectionId || '').trim() : '';

  useEffect(() => {
    setInspectionId(routeInspectionId || '');
  }, [routeInspectionId]);

  useEffect(() => {
    if (!isCreatePage) return;
    if (lastCreateResetRef.current === 'create') return;
    setInspectionId('');
    setWorkOrderId('');
    setStageId('');
    reportForm.resetFields();
    setDimensionRows([]);
    setMeasurementValues({});
    setVisibleRowCount(DEFAULT_DIMENSION_ROW_COUNT);
    lastCreateResetRef.current = 'create';
  }, [isCreatePage]);

  useEffect(() => {
    setVisibleRowCount(DEFAULT_DIMENSION_ROW_COUNT);
  }, [stageId]);

  useEffect(() => {
    if (!isCreatePage) return;
    if (lastWorkOrderResetRef.current === workOrderId) return;

    const previousWorkOrderId = lastWorkOrderResetRef.current;
    lastWorkOrderResetRef.current = workOrderId;

    if (!previousWorkOrderId) {
      setIsGeneratingReportNo(false);
      lastReportMetadataSyncRef.current = '';
      lastDefaultFillRef.current = '';
      measurementCellRef.current = {
        key: '',
        input: null,
      };
      dimensionTextFieldRef.current = null;
      return;
    }

    reportForm.resetFields();
    setMeasurementValues({});
    setIsGeneratingReportNo(false);
    lastReportMetadataSyncRef.current = '';
    lastDefaultFillRef.current = '';
    measurementCellRef.current = {
      key: '',
      input: null,
    };
    dimensionTextFieldRef.current = null;
  }, [isCreatePage, reportForm, workOrderId]);

  const { data: workOrders = EMPTY_ARRAY, isLoading: loadingWorkOrders } = useWorkOrders();
  const { data: workOrder, isLoading: loadingWorkOrder } = useWorkOrder(workOrderId);
  const { data: salesOrder, isLoading: loadingSalesOrder } = useSalesOrder(workOrder?.sales_order_id);
  const { data: stages = EMPTY_ARRAY, isLoading: loadingStages } = useWorkOrderStages(workOrderId);
  const { data: users = EMPTY_ARRAY } = useUsers({ is_active: true });
  const { data: equipmentOptions = EMPTY_ARRAY, isLoading: loadingEquipmentOptions } = useQuery({
    queryKey: ['quality', 'tmmde-instrument-options'],
    queryFn: async () => {
      const { rows } = await calibrationService.listEquipment();
      return rows.map((equipment) => {
        const usable = equipment.calibration_status === 'CALIBRATED' || equipment.calibration_status === 'DUE_SOON';
        const printLabel = [
          equipment.asset_code,
          equipment.asset_name || undefined,
          equipment.serial_number ? `SN: ${equipment.serial_number}` : undefined,
        ]
          .filter(Boolean)
          .join(' - ');
        return {
          label: usable ? printLabel : `${printLabel} (${tmmdeStatusLabel(equipment.calibration_status)})`,
          printLabel,
          value: equipment.id,
        };
      });
    },
  });
  const instrumentPrintLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const o of equipmentOptions as Array<{ value: string; printLabel: string }>) map[o.value] = o.printLabel;
    return map;
  }, [equipmentOptions]);
  const { runWithOverride, overrideModal } = useTmmdeUseOverride();
  const { data: dimensions = EMPTY_ARRAY, isLoading: loadingDimensions, refetch: refetchDimensions } = useStageDimensions(stageId);
  const { data: stageInspection, isLoading: loadingInspection, refetch: refetchInspection } = useStageInspectionByStage(stageId);
  const { data: directInspection } = useStageInspection(inspectionId, !!inspectionId);
  const { data: sourceInspection } = useStageInspection(prefillSourceId, Boolean(prefillSourceId));
  const inspection = directInspection || (!isCreatePage ? stageInspection : undefined);
  const { data: measurements = EMPTY_ARRAY, refetch: refetchMeasurements } = useStageInspectionMeasurements(inspection?.id || '');
  const { data: sourceMeasurements = EMPTY_ARRAY } = useStageInspectionMeasurements(sourceInspection?.id || '');
  const { data: inspectionRows = EMPTY_ARRAY, isLoading: loadingInspectionRows } = useQuery({
    queryKey: ['quality', 'dimensional-inspections'],
    queryFn: async () => {
      const response = await sciService.getAll();
      return (response.data?.data || response.data || []) as StageCompletionInspection[];
    },
    enabled: !isStandaloneForm,
  });

  useEffect(() => {
    if (!directInspection) return;
    setWorkOrderId(directInspection.work_order_id || '');
    setStageId(directInspection.work_order_stage_id || '');
  }, [directInspection]);

  useEffect(() => {
    if (!isCreatePage || !sourceInspection) return;
    setWorkOrderId(sourceInspection.work_order_id || '');
    setStageId(sourceInspection.work_order_stage_id || '');
  }, [isCreatePage, sourceInspection]);

  const createInspectionMutation = useCreateStageInspection();
  const startInspectionMutation = useStartInspection();
  const recordMeasurementsMutation = useRecordMeasurements();
  const updateReportMetadataMutation = useUpdateReportMetadata();
  const addDimensionMutation = useAddStageDimension();
  const cloneInspectionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await sciService.clone(id);
      return (response.data?.data || response.data) as StageCompletionInspection;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'dimensional-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      message.success('Dimensional inspection cloned');
      navigate(`/qms/dimensional-inspection/${data.id}/edit`);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to clone dimensional inspection');
    },
  });
  const revisionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await sciService.createRevision(id);
      return (response.data?.data || response.data) as StageCompletionInspection;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'dimensional-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      message.success('Dimensional inspection revision created');
      navigate(`/qms/dimensional-inspection/${data.id}/edit`);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to create revision');
    },
  });
  const deleteInspectionMutation = useMutation({
    mutationFn: async (id: string) => {
      await sciService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'dimensional-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      message.success('Dimensional inspection deleted');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete dimensional inspection');
    },
  });

  const selectedStage = useMemo(() => stages.find((s: WorkOrderStage) => s.id === stageId), [stages, stageId]);
  const workOrderPrimaryLineItem = useMemo(() => {
    if (!workOrder?.lineItems?.length) return undefined;
    return workOrder.lineItems[0];
  }, [workOrder?.lineItems]);
  const selectedLineItem = useMemo(() => {
    if (!workOrder?.lineItems?.length) return undefined;
    if (selectedStage?.work_order_line_item_id) {
      return workOrder.lineItems.find((line) => line.id === selectedStage.work_order_line_item_id);
    }
    return workOrder.lineItems[0];
  }, [selectedStage?.work_order_line_item_id, workOrder?.lineItems]);
  const resolvedClientName = useMemo(() => {
    const salesOrderClient = String(salesOrder?.customer_name ?? '').trim();
    if (salesOrderClient) return salesOrderClient;
    const workOrderClient = String(workOrder?.customer_name ?? '').trim();
    return workOrderClient || undefined;
  }, [salesOrder?.customer_name, workOrder?.customer_name]);
  const currentUserDisplayName = useMemo(() => getUserDisplayName(user), [user]);
  const userNameById = useMemo(
    () =>
      Object.fromEntries(
        users.map((item: any) => [String(item.id), getUserDisplayName(item)])
      ) as Record<string, string>,
    [users]
  );

  /** Planned quantity for the work order line (or work order) — upper cap for report Qty and columns. */
  const linePlannedQty = useMemo(() => {
    if (!workOrder) return 0;
    if (selectedStage?.work_order_line_item_id && workOrder.lineItems?.length) {
      const lineItem = workOrder.lineItems.find((li: any) => li.id === selectedStage.work_order_line_item_id);
      return lineItem ? Math.max(1, Math.floor(Number(lineItem.planned_quantity))) : Math.max(1, Math.floor(Number(workOrder.planned_quantity)));
    }
    return Math.max(1, Math.floor(Number(workOrder.planned_quantity)));
  }, [workOrder, selectedStage]);

  const watchedReportQty = Form.useWatch('qty', reportForm);

  const columnCount = useMemo(() => {
    const line = linePlannedQty;
    const raw = watchedReportQty;
    const q = raw != null && raw !== '' ? Math.floor(Number(raw)) : NaN;
    const effective = Number.isFinite(q) && q > 0 ? q : line;
    return Math.max(1, Math.min(line, effective));
  }, [watchedReportQty, linePlannedQty]);

  const prefillInspection = !inspection && isCreatePage ? sourceInspection : undefined;
  const activeReportSource = inspection || prefillInspection;
  const activeMeasurements = inspection ? measurements : prefillInspection ? sourceMeasurements : [];
  const canEdit = Boolean(inspection || (isCreatePage && stageId));
  const filteredInspectionRows = useMemo(() => {
    const dimensionalRows = inspectionRows.filter((row) => {
      const meta = (row.report_metadata || {}) as ReportMetadata;
      return Boolean(
        String(meta.report_no || '').trim() ||
        String(meta.date_of_inspection || '').trim() ||
        String(meta.client || '').trim() ||
        String(meta.drawing_reference || '').trim() ||
        String(meta.part_no_description || '').trim() ||
        String(meta.inspected_by || '').trim()
      );
    });

    const keyword = searchText.trim().toLowerCase();
    if (!keyword) return dimensionalRows;
    return dimensionalRows.filter((row) => {
      const meta = (row.report_metadata || {}) as ReportMetadata;
      const haystack = [
        row.inspection_number,
        row.workOrder?.work_order_number,
        row.workOrderStage?.stage_code,
        row.workOrderStage?.stage_name,
        row.status,
        row.result,
        meta.report_no,
        meta.job_card_no,
        meta.client,
        meta.part_no_description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [inspectionRows, searchText]);

  const listColumns: ColumnsType<StageCompletionInspection> = useMemo(
    () => [
      {
        title: 'Report No.',
        key: 'report_no',
        width: 160,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).report_no || '—',
      },
      {
        title: 'Date of Inspection',
        key: 'date_of_inspection',
        width: 160,
        render: (_, row) => {
          const value = ((row.report_metadata || {}) as ReportMetadata).date_of_inspection;
          return value ? dayjs(value).format('DD MMM YYYY').toUpperCase() : '—';
        },
      },
      {
        title: 'Client',
        key: 'client',
        width: 300,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).client || '—',
      },
      {
        title: 'Drawing Ref.',
        key: 'drawing_reference',
        width: 180,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).drawing_reference || '—',
      },
      {
        title: 'Part No./Description',
        key: 'part_no_description',
        width: 220,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).part_no_description || '—',
      },
      {
        title: 'Inspected By',
        key: 'inspected_by',
        width: 190,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).inspected_by || '—',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 210,
        render: (_, row) => (
          <Space size="small" wrap={false}>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/qms/dimensional-inspection/${row.id}/edit`)}
              />
            </Tooltip>
            <Tooltip title="Clone">
              <Button
                size="small"
                icon={<CopyOutlined />}
                loading={cloneInspectionMutation.isPending && cloneInspectionMutation.variables === row.id}
                onClick={() => cloneInspectionMutation.mutate(row.id)}
              />
            </Tooltip>
            <Tooltip title="Revision">
              <Button
                size="small"
                icon={<RetweetOutlined />}
                loading={revisionMutation.isPending && revisionMutation.variables === row.id}
                onClick={() => revisionMutation.mutate(row.id)}
              />
            </Tooltip>
            <Tooltip title="Print">
              <Button
                size="small"
                icon={<PrinterOutlined />}
                onClick={() => openDimensionalInspectionPrint(row.id)}
              />
            </Tooltip>
            <Popconfirm
              title="Delete dimensional inspection?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteInspectionMutation.mutate(row.id)}
            >
              <Tooltip title="Delete">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteInspectionMutation.isPending && deleteInspectionMutation.variables === row.id}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [
      cloneInspectionMutation,
      deleteInspectionMutation,
      navigate,
      revisionMutation,
    ]
  );

  const ensurePreviewReportNo = useCallback(async () => {
    if (!user?.company_id || !workOrderId) return;
    const currentReportNo = String(reportForm.getFieldValue('report_no') ?? '').trim();
    if (currentReportNo) return;

    setIsGeneratingReportNo(true);
    try {
      const reportNo = await settingsService.previewDocumentNumber(user.company_id, 'DIM');
      if (!String(reportForm.getFieldValue('report_no') ?? '').trim()) {
        reportForm.setFieldValue('report_no', reportNo);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Failed to preview report number');
    } finally {
      setIsGeneratingReportNo(false);
    }
  }, [reportForm, user?.company_id, workOrderId]);

  const syncDimensionTextCaret = useCallback((rowKey: string, field: 'dimension_name' | 'requirement_text', el: HTMLInputElement | HTMLTextAreaElement) => {
    dimensionTextFieldRef.current = {
      rowKey,
      field,
      input: el,
      selStart: el.selectionStart ?? 0,
      selEnd: el.selectionEnd ?? 0,
    };
  }, []);

  const bindDimensionSymbolField = useCallback(
    (rowKey: string, field: 'dimension_name' | 'requirement_text') => ({
      onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        syncDimensionTextCaret(rowKey, field, e.target),
      onSelect: (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        syncDimensionTextCaret(rowKey, field, e.currentTarget),
      onKeyUp: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        syncDimensionTextCaret(rowKey, field, e.currentTarget),
      onMouseUp: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        syncDimensionTextCaret(rowKey, field, e.currentTarget),
    }),
    [syncDimensionTextCaret]
  );

  const insertEngineeringSymbol = useCallback(
    (symbol: string) => {
      if (!canEdit) return;

      if (dimensionTextFieldRef.current) {
        const ref = dimensionTextFieldRef.current;
        const row = dimensionRows.find((item) => item.key === ref.rowKey);
        const cur = String(row?.[ref.field] ?? '');
        const start = Math.min(Math.max(0, ref.selStart), cur.length);
        const end = Math.min(Math.max(start, ref.selEnd), cur.length);
        const next = cur.slice(0, start) + symbol + cur.slice(end);
        setDimensionRows((prev) =>
          prev.map((item) =>
            item.key === ref.rowKey ? { ...item, [ref.field]: next } : item
          )
        );
        const pos = start + symbol.length;
        ref.selStart = pos;
        ref.selEnd = pos;
        requestAnimationFrame(() => {
          try {
            ref.input.focus();
            ref.input.setSelectionRange(pos, pos);
          } catch {
            /* ignore */
          }
        });
        return;
      }

      const { key, input } = measurementCellRef.current;
      if (!key) {
        message.info('Focus a measurement cell first.');
        return;
      }
      setMeasurementValues((prev) => {
        const cur = prev[key] ?? '';
        const start =
          input && document.activeElement === input ? input.selectionStart ?? cur.length : cur.length;
        const end = input && document.activeElement === input ? input.selectionEnd ?? start : start;
        const next = cur.slice(0, start) + symbol + cur.slice(end);
        const pos = start + symbol.length;
        requestAnimationFrame(() => {
          input?.focus();
          try {
            input?.setSelectionRange(pos, pos);
          } catch {
            /* ignore */
          }
        });
        return { ...prev, [key]: next };
      });
    },
    [canEdit, dimensionRows]
  );

  React.useEffect(() => {
    if (!activeReportSource?.report_metadata) return;
    const sourceMeta = activeReportSource.report_metadata as ReportMetadata;
    const meta: ReportMetadata = {
      ...sourceMeta,
      report_no: inspection ? sourceMeta.report_no : undefined,
      revision:
        !inspection && prefillMode === 'revision'
          ? incrementRevisionValue(sourceMeta.revision)
          : sourceMeta.revision,
    };
    const syncKey = JSON.stringify({
      reportSourceId: activeReportSource.id,
      reportNo: meta.report_no ?? '',
      revision: meta.revision ?? '',
      inspectedBy: meta.inspected_by ?? '',
      qty: meta.qty ?? '',
      dateOfInspection: meta.date_of_inspection ?? '',
      prefillMode: prefillMode ?? '',
    });
    if (lastReportMetadataSyncRef.current === syncKey) return;

    const inspectedByRaw = String(meta.inspected_by ?? '').trim();
    const inspectedByDisplay =
      inspectedByRaw && userNameById[inspectedByRaw]
        ? userNameById[inspectedByRaw]
        : inspectedByRaw === String(user?.id ?? '')
          ? currentUserDisplayName || inspectedByRaw
          : inspectedByRaw;
    reportForm.setFieldsValue({
      report_no: meta.report_no,
      job_card_no: meta.job_card_no,
      drawing_reference: meta.drawing_reference,
      revision: meta.revision,
      client: meta.client,
      part_no_description: meta.part_no_description,
      heat_number: meta.heat_number,
      customer_job_no: meta.customer_job_no,
      material: meta.material,
      inspected_by: inspectedByDisplay,
      date_of_inspection: meta.date_of_inspection ? dayjs(meta.date_of_inspection) : dayjs(),
      qty: meta.qty ?? linePlannedQty,
      po_line_no: meta.po_line_no,
      client_po_no: meta.client_po_no,
      instruments_used: instrumentFormValue(meta),
      remarks: meta.remarks,
      qc_approved_by: meta.qc_approved_by,
      qc_approval_date: meta.qc_approval_date ? dayjs(meta.qc_approval_date) : undefined,
    });
    lastReportMetadataSyncRef.current = syncKey;
  }, [
    activeReportSource?.report_metadata,
    activeReportSource?.id,
    currentUserDisplayName,
    inspection,
    linePlannedQty,
    prefillMode,
    user?.id,
    userNameById,
  ]);

  React.useEffect(() => {
    if (!inspection?.id && !isCreatePage) return;
    if (inspection?.report_metadata != null) return;
    if (prefillInspection?.report_metadata != null) return;
    const syncKey = JSON.stringify({
      inspectionId: inspection?.id ?? '',
      isCreatePage,
      linePlannedQty,
      inspectedBy: currentUserDisplayName || user?.username || user?.email || '',
    });
    if (lastDefaultFillRef.current === syncKey) return;

    reportForm.setFieldsValue({
      qty: linePlannedQty,
      date_of_inspection: dayjs(),
      inspected_by: currentUserDisplayName || user?.username || user?.email || '',
    });
    lastDefaultFillRef.current = syncKey;
  }, [
    currentUserDisplayName,
    inspection?.id,
    inspection?.report_metadata,
    isCreatePage,
    linePlannedQty,
    prefillInspection?.report_metadata,
    user?.email,
    user?.username,
  ]);

  React.useEffect(() => {
    if (!workOrderId) return;
    if (String(reportForm.getFieldValue('report_no') ?? '').trim()) return;
    void ensurePreviewReportNo();
  }, [ensurePreviewReportNo, workOrderId]);

  React.useEffect(() => {
    if (!inspection?.id && !isCreatePage) return;

    const currentValues = reportForm.getFieldsValue([
      'job_card_no',
      'client',
      'customer_job_no',
      'client_po_no',
      'remarks',
      'qc_approved_by',
      'qc_approval_date',
    ]) as Record<string, unknown>;

    const defaultValues: Record<string, unknown> = {
      job_card_no: workOrder?.work_order_number || undefined,
      client: resolvedClientName,
      customer_job_no: workOrder?.project_name || salesOrder?.project_name || workOrder?.sales_order_number || undefined,
      qc_approved_by: currentUserDisplayName || user?.username || user?.email || undefined,
      qc_approval_date: dayjs(),
      client_po_no: salesOrder?.customer_po_number || undefined,
      remarks:
        workOrder?.wo_customer_requirements_notes ||
        workOrder?.client_quality_requirements ||
        undefined,
    };

    const nextValues: Record<string, unknown> = {};
    Object.entries(defaultValues).forEach(([key, value]) => {
      const currentValue = currentValues[key];
      const isEmpty =
        currentValue == null ||
        (typeof currentValue === 'string' && currentValue.trim() === '');

      if (isEmpty && value != null && String(value).trim() !== '') {
        nextValues[key] = value;
      }

      if (
        key === 'qc_approval_date' &&
        currentValue == null &&
        dayjs.isDayjs(value)
      ) {
        nextValues[key] = value;
      }
    });

    if (Object.keys(nextValues).length > 0) {
      reportForm.setFieldsValue(nextValues);
    }
  }, [
    currentUserDisplayName,
    inspection?.id,
    isCreatePage,
    salesOrder?.customer_po_number,
    salesOrder?.project_name,
    resolvedClientName,
    user?.email,
    user?.username,
    workOrder?.client_quality_requirements,
    workOrder?.customer_name,
    workOrder?.project_name,
    workOrder?.sales_order_number,
    workOrder?.wo_customer_requirements_notes,
    workOrder?.work_order_number,
  ]);

  React.useEffect(() => {
    if (!inspection?.id && !isCreatePage) return;
    if (!workOrderId) return;
    if (inspection?.report_metadata != null) return;
    if (prefillInspection?.report_metadata != null) return;

    const stageLineItem = selectedLineItem || workOrderPrimaryLineItem;
    const partText = [stageLineItem?.part_number, stageLineItem?.part_description || stageLineItem?.item_description]
      .filter((value) => String(value || '').trim())
      .join(' / ');

    const defaultValues: Record<string, unknown> = {
      drawing_reference: stageLineItem?.drawing_reference_number || undefined,
      revision: String((stageLineItem as any)?.revision ?? '').trim() || '0',
      part_no_description: partText || undefined,
      heat_number: stageLineItem?.heat_number || undefined,
      material: String((stageLineItem as any)?.material ?? '').trim() || undefined,
      po_line_no:
        stageLineItem?.line_number != null ? String(stageLineItem.line_number) : undefined,
    };
    reportForm.setFieldsValue(defaultValues);
  }, [
    inspection?.id,
    inspection?.report_metadata,
    isCreatePage,
    prefillInspection?.report_metadata,
    reportForm,
    selectedLineItem,
    workOrderId,
    workOrderPrimaryLineItem,
  ]);

  React.useEffect(() => {
    setDimensionRows((prev) => {
      const previousPersistedById = new Map(
        prev.filter((row) => row.id).map((row) => [row.id as string, row])
      );
      const existingDraftRows = prev.filter((row) => !row.isPersisted);

      const nextRows: DimensionTableRow[] = dimensions
        .slice()
        .sort((a, b) => a.sequence_number - b.sequence_number)
        .map((dim) => {
          const previousRow = previousPersistedById.get(dim.id);
          return {
            key: dim.id,
            id: dim.id,
            dimension_name: previousRow?.dimension_name ?? dim.dimension_name,
            requirement_text: previousRow?.requirement_text ?? requirementDisplay(dim),
            sequence_number: dim.sequence_number,
            isPersisted: true,
          };
        });

      const targetRowCount = Math.max(visibleRowCount, nextRows.length, 1);
      let draftIndex = 0;
      while (nextRows.length < targetRowCount) {
        const existingDraft = existingDraftRows[draftIndex];
        nextRows.push(
          existingDraft
            ? { ...existingDraft, sequence_number: nextRows.length + 1 }
            : makeDraftRow(nextRows.length + 1)
        );
        draftIndex += 1;
      }

      const validPrefixes = new Set(nextRows.map((row) => `${row.key}_`));
      setMeasurementValues((prevMeasurements) => {
        const nextMeasurements: Record<string, string> = {};
        Object.entries(prevMeasurements).forEach(([key, value]) => {
          if (Array.from(validPrefixes).some((prefix) => key.startsWith(prefix))) {
            nextMeasurements[key] = value;
          }
        });
        return nextMeasurements;
      });

      return nextRows.map((row, index) => ({
        ...row,
        sequence_number: index + 1,
      }));
    });
  }, [dimensions, visibleRowCount]);

  React.useEffect(() => {
    if (!dimensions.length || !columnCount) return;
    setMeasurementValues((prev) => {
      const next = { ...prev };
      dimensions.forEach((dim: WorkOrderStageDimension) => {
        for (let u = 1; u <= columnCount; u++) {
          const m = activeMeasurements.find(
            (x: StageInspectionMeasurement) =>
              x.stage_dimension_id === dim.id && x.unit_sequence === u
          );
          const key = `${dim.id}_${u}`;
          if (m != null) {
            const text = m.actual_text != null && String(m.actual_text).trim() !== '' ? String(m.actual_text).trim() : '';
            if (text) next[key] = text;
            else if (m.measured_value != null && !Number.isNaN(Number(m.measured_value))) next[key] = formatMeasurementDisplayValue(m.measured_value);
          }
        }
      });
      return next;
    });
  }, [activeMeasurements, columnCount, dimensions]);

  const handleStartInspection = async () => {
    if (!stageId) return;
    try {
      let insp = inspection;
      if (!insp) {
        insp = await createInspectionMutation.mutateAsync(stageId);
        refetchInspection();
      }
      if (insp && insp.status === 'Pending') {
        await startInspectionMutation.mutateAsync(insp.id);
        refetchInspection();
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to start inspection');
    }
  };

  const saveReportInfo = async (): Promise<StageCompletionInspection | null> => {
    const values = await reportForm.validateFields();
    let targetInspection = inspection;
    if (!targetInspection) {
      if (!stageId) {
        message.error('Select a stage before saving.');
        return null;
      }
      targetInspection = await createInspectionMutation.mutateAsync(stageId);
      setInspectionId(targetInspection.id);
    }

    const report_metadata: ReportMetadata = {
      report_no: values.report_no,
      job_card_no: values.job_card_no,
      drawing_reference: values.drawing_reference,
      revision: values.revision,
      client: values.client,
      part_no_description: values.part_no_description,
      heat_number: values.heat_number,
      customer_job_no: values.customer_job_no,
      material: values.material,
      inspected_by: values.inspected_by || currentUserDisplayName || user?.username || user?.email,
      date_of_inspection: values.date_of_inspection?.toISOString?.()?.slice(0, 10) || dayjs().format('YYYY-MM-DD'),
      qty: values.qty != null ? Math.floor(Number(values.qty)) : linePlannedQty,
      po_line_no: values.po_line_no,
      client_po_no: values.client_po_no,
      ...instrumentMetadata(
        values.instruments_used,
        instrumentPrintLabelById,
        (targetInspection.report_metadata ?? undefined) as ReportMetadata | undefined
      ),
      remarks: values.remarks,
      qc_approved_by: values.qc_approved_by,
      qc_approval_date: values.qc_approval_date?.toISOString?.()?.slice(0, 10),
    };

    const savedInspection = targetInspection;
    await runWithOverride((overrideReason) =>
      updateReportMetadataMutation.mutateAsync({
        id: savedInspection.id,
        report_metadata: report_metadata as Record<string, unknown>,
        tmmde_override_reason: overrideReason,
      })
    );
    refetchInspection();
    if (isCreatePage && targetInspection?.id) {
      navigate(`/qms/dimensional-inspection/${targetInspection.id}/edit`, { replace: true });
    }
    return targetInspection;
  };

  const saveMeasurements = async (targetInspection: StageCompletionInspection) => {
    if (targetInspection.status === 'Pending') {
      await startInspectionMutation.mutateAsync(targetInspection.id);
      refetchInspection();
    }

    const lineId = selectedStage?.work_order_line_item_id;
    if (!lineId) {
      message.error('This stage has no work order line; cannot save dimension rows.');
      return false;
    }

    const populatedRows = dimensionRows.filter(
      (row) => row.dimension_name.trim() !== '' || row.requirement_text.trim() !== ''
    );
    if (populatedRows.some((row) => row.dimension_name.trim() === '' || row.requirement_text.trim() === '')) {
      message.error('Fill both Description and Required Dimension/Tolerance for each used row.');
      return false;
    }

    const dimensionIdByRowKey: Record<string, string> = {};
    try {
      for (const [index, row] of populatedRows.entries()) {
        const payload = {
          work_order_line_item_id: lineId,
          sequence_number: index + 1,
          dimension_name: row.dimension_name.trim(),
          specification_text: row.requirement_text.trim(),
          nominal_value: 0,
          unit: 'mm',
          tolerance_type: 'Nominal' as const,
          is_mandatory: true,
        };

        if (row.id) {
          await workOrderStageService.updateDimension(row.id, payload);
          dimensionIdByRowKey[row.key] = row.id;
        } else {
          const response = await addDimensionMutation.mutateAsync({ stageId, data: payload });
          dimensionIdByRowKey[row.key] = response.id;
        }
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to save dimension rows');
      return false;
    }

    const payload: Array<{
      stage_dimension_id: string;
      unit_sequence: number;
      measured_value?: number | null;
      actual_text?: string | null;
      measured_by?: string;
    }> = [];
    const numericPattern = /^-?\d+(\.\d+)?$/;
    populatedRows.forEach((row) => {
      const dimensionId = dimensionIdByRowKey[row.key] || row.id;
      if (!dimensionId) return;
      for (let u = 1; u <= columnCount; u++) {
        const raw = measurementValues[`${row.key}_${u}`];
        if (raw === undefined || raw === null) continue;
        const trimmed = String(raw).trim();
        if (!trimmed) continue;
        if (numericPattern.test(trimmed)) {
          payload.push({
            stage_dimension_id: dimensionId,
            unit_sequence: u,
            measured_value: Number(trimmed),
            measured_by: user?.id,
          });
        } else {
          payload.push({
            stage_dimension_id: dimensionId,
            unit_sequence: u,
            actual_text: trimmed,
            measured_by: user?.id,
          });
        }
      }
    });
    if (payload.length === 0) {
      return true;
    }
    try {
      if (payload.length > 0) {
        await recordMeasurementsMutation.mutateAsync({ id: targetInspection.id, measurements: payload });
      }
      await refetchDimensions();
      refetchMeasurements();
      return true;
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to save measurements');
      return false;
    }
  };

  const handleSaveAll = async () => {
    try {
      const targetInspection = await saveReportInfo();
      if (!targetInspection) return;
      const measurementsSaved = await saveMeasurements(targetInspection);
      if (!measurementsSaved) return;
      message.success('Dimensional inspection saved successfully');
      navigate('/qms/dimensional-inspection');
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Failed to save dimensional inspection');
    }
  };

  const appendBlankRows = useCallback((rows: DimensionTableRow[], minCount: number) => {
    const nextRows = [...rows];
    while (nextRows.length < minCount) {
      nextRows.push(makeDraftRow(nextRows.length + 1));
    }
    return nextRows.map((row, index) => ({ ...row, sequence_number: index + 1 }));
  }, []);

  const handleAddRow = () => {
    setVisibleRowCount((prev) => prev + 1);
    setDimensionRows((prev) => [...prev, makeDraftRow(prev.length + 1)]);
  };

  const handleRowChange = useCallback((rowKey: string, field: 'dimension_name' | 'requirement_text', value: string) => {
    setDimensionRows((prev) =>
      prev.map((row) => (row.key === rowKey ? { ...row, [field]: value } : row))
    );
  }, []);

  const handleDeleteDimension = async (dimensionId: string) => {
    try {
      await workOrderStageService.deleteDimension(dimensionId);
      queryClient.invalidateQueries({ queryKey: ['stage-dimensions', stageId] });
      refetchDimensions();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to delete dimension');
    }
  };

  const handleDeleteRow = async (row: DimensionTableRow) => {
    if (dimensionRows.length <= 1) {
      message.info('At least one row must remain.');
      return;
    }

    setVisibleRowCount((prev) => Math.max(1, prev - 1));

    if (row.id) {
      await handleDeleteDimension(row.id);
      return;
    }
    setDimensionRows((prev) => appendBlankRows(prev.filter((item) => item.key !== row.key), Math.max(1, visibleRowCount - 1)));
    setMeasurementValues((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${row.key}_`)) delete next[key];
      });
      return next;
    });
    if (dimensionTextFieldRef.current?.rowKey === row.key) {
      dimensionTextFieldRef.current = null;
    }
  };

  const toleranceDisplay = (dim: WorkOrderStageDimension) => {
    if (dim.tolerance_type === 'Plus-Minus') {
      const plus = dim.tolerance_plus ?? 0;
      const minus = dim.tolerance_minus ?? 0;
      return `${dim.nominal_value} ±${plus}/${minus} ${dim.unit}`;
    }
    if (dim.tolerance_type === 'Min-Max' && dim.min_value != null && dim.max_value != null) {
      return `${dim.min_value} – ${dim.max_value} ${dim.unit}`;
    }
    return `${dim.nominal_value} ${dim.unit}`;
  };

  const requirementDisplay = (dim: WorkOrderStageDimension) => {
    if (dim.specification_text != null && String(dim.specification_text).trim() !== '') {
      return String(dim.specification_text).trim();
    }
    return toleranceDisplay(dim)
      .replace(/Â±/g, '±')
      .replace(/â€“/g, '-')
      .replace(/âˆ’/g, '-');
  };

  const tableColumns: ColumnsType<any> = useMemo(() => {
    const cols: ColumnsType<any> = [
      {
        title: 'S.NO.',
        key: 'sno',
        width: 72,
        render: (_: any, __: any, index: number) => <span className="dimension-measurements__serial">{index + 1}</span>,
      },
      {
        title: 'DESCRIPTION',
        dataIndex: 'dimension_name',
        key: 'dimension_name',
        width: 340,
        render: (value: string, row: DimensionTableRow) => (
          <Input
            value={value}
            className="dimension-measurements__editable-input"
            placeholder="e.g., Total Length"
            onChange={(e) => handleRowChange(row.key, 'dimension_name', e.target.value)}
            disabled={!canEdit}
            {...bindDimensionSymbolField(row.key, 'dimension_name')}
          />
        ),
      },
      {
        title: 'REQUIRED DIMENSION/TOLERANCE',
        key: 'tolerance',
        width: 360,
        render: (_: any, row: DimensionTableRow) => (
          <Input
            value={row.requirement_text}
            onChange={(e) => handleRowChange(row.key, 'requirement_text', e.target.value)}
            disabled={!canEdit}
            {...bindDimensionSymbolField(row.key, 'requirement_text')}
            className="dimension-measurements__editable-input"
            placeholder={'e.g., 14.50" ± 0.030'}
          />
        ),
      },
    ];
    for (let u = 1; u <= columnCount; u++) {
      cols.push({
        title: String(u),
        key: `actual_${u}`,
        width: 110,
        render: (_: any, row: DimensionTableRow) => (
          <Input
            size="middle"
            className="dimension-measurements__actual-input"
            style={{ width: '100%', minHeight: TOUCH_MIN_HEIGHT }}
            value={measurementValues[`${row.key}_${u}`] ?? ''}
            placeholder={String(u)}
            onChange={(e) => {
              const v = e.target.value;
              setMeasurementValues((prev) => ({ ...prev, [`${row.key}_${u}`]: v }));
            }}
            onFocus={(e) => {
              dimensionTextFieldRef.current = null;
              measurementCellRef.current = {
                key: `${row.key}_${u}`,
                input: e.target as HTMLInputElement,
              };
            }}
            disabled={!canEdit}
          />
        ),
      });
    }
    cols.push({
      title: 'ACTIONS',
      key: 'actions',
      width: 96,
      render: (_: any, row: DimensionTableRow) =>
        canEdit ? (
          <Popconfirm
            title="Delete this row?"
            onConfirm={() => void handleDeleteRow(row)}
          >
            <Button
              type="text"
              danger
              size="middle"
              icon={<DeleteOutlined />}
              className="dimension-measurements__delete-button"
              style={{ minHeight: TOUCH_MIN_HEIGHT }}
            />
          </Popconfirm>
        ) : null,
    });
    return cols;
  }, [columnCount, measurementValues, canEdit, bindDimensionSymbolField, handleDeleteRow, handleRowChange]);

  const loading =
    loadingWorkOrders ||
    (workOrderId && loadingWorkOrder) ||
    (workOrderId && loadingStages) ||
    (!!workOrder?.sales_order_id && loadingSalesOrder);

  return (
    <div className="dimensional-inspection-page" style={{ padding: '16px 12px', maxWidth: 1600, margin: '0 auto' }}>
      {overrideModal}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          {isStandaloneForm ? formPageTitle : 'Dimensional Inspection'}
        </Title>
        {isStandaloneForm ? (
          <Link to="/qms/dimensional-inspection">
            <Button icon={<ArrowLeftOutlined />}>
              Back To List
            </Button>
          </Link>
        ) : null}
      </Space>

      {!isStandaloneForm ? (
      <Card style={{ marginBottom: 16 }}>
        <Space
          wrap
          style={{
            marginBottom: 16,
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Search
            allowClear
            placeholder="Search inspection no, report no, work order, stage, client..."
            style={{ width: 360 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={setSearchText}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/qms/dimensional-inspection/new')}
          >
            Add Dimensional Inspection
          </Button>
        </Space>

        <Table<StageCompletionInspection>
          rowKey="id"
          loading={loadingInspectionRows}
          columns={listColumns}
          dataSource={filteredInspectionRows}
          scroll={{ x: 'max-content' }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      </Card>
      ) : (
      <>
      <div ref={editorRef}>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={12}>
            <Form.Item label="Work Order" style={{ marginBottom: 0 }}>
              <Select
                placeholder="Select Work Order"
                showSearch
                optionFilterProp="label"
                value={workOrderId || undefined}
                onChange={(v) => {
                  setInspectionId('');
                  setWorkOrderId(v || '');
                  setStageId('');
                }}
                loading={loadingWorkOrders}
                options={workOrders.map((wo: any) => ({ label: wo.work_order_number || wo.id, value: wo.id }))}
                style={{ width: '100%', minHeight: TOUCH_MIN_HEIGHT }}
                size="large"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12}>
            <Form.Item label="Stage" style={{ marginBottom: 0 }}>
              <Select
                placeholder="Select Stage"
                value={stageId || undefined}
                onChange={(value) => {
                  setInspectionId('');
                  setStageId(value || '');
                }}
                loading={loadingStages}
                disabled={!workOrderId}
                style={{ width: '100%', minHeight: TOUCH_MIN_HEIGHT }}
                size="large"
                allowClear
                options={stages.map((s: WorkOrderStage) => ({
                  label: `${s.stage_name || s.stage_code} (${s.stage_code})`,
                  value: s.id,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      </div>

      {!workOrderId ? (
        <Alert message="Select a Work Order to continue." type="info" showIcon />
      ) : loading ? (
        <Spin tip="Loading..." />
      ) : (
        <>
          {stageId && !inspection && !isCreatePage && (
            <Card style={{ marginBottom: 16 }}>
              <Alert
                message="No inspection for this stage yet."
                description="Create and start a dimensional inspection to enter report information and measurements. Note: The stage may need to be completed first."
                type="info"
                showIcon
                action={
                  <Button
                    type="primary"
                    size="large"
                    icon={<PlayCircleOutlined />}
                    onClick={handleStartInspection}
                    loading={createInspectionMutation.isPending || startInspectionMutation.isPending}
                    style={{ minHeight: TOUCH_MIN_HEIGHT }}
                  >
                    Start Dimensional Inspection
                  </Button>
                }
              />
            </Card>
          )}

          {(inspection || isCreatePage) && (
            <>
              <Card title="Report Information" style={{ marginBottom: 16 }}>
                <Form form={reportForm} layout="vertical">
                  <Row gutter={[16, 12]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="report_no" label="Report No." rules={[{ required: true, message: 'Required' }]}>
                        <Input
                          size="large"
                          style={{ minHeight: TOUCH_MIN_HEIGHT }}
                          readOnly
                          suffix={isGeneratingReportNo ? <Spin size="small" /> : null}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="job_card_no" label="Job Card No." rules={[{ required: true, message: 'Required' }]}>
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="drawing_reference" label="Drawing Reference" rules={[{ required: true, message: 'Required' }]}>
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="revision" label="Revision">
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="client" label="Client" rules={[{ required: true, message: 'Required' }]}>
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="part_no_description" label="Part No./Description" rules={[{ required: true, message: 'Required' }]}>
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="heat_number" label="Heat Number">
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="customer_job_no" label="Customer Job No.">
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="material" label="Material">
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item
                        name="inspected_by"
                        label="Inspected By"
                        initialValue={currentUserDisplayName || user?.username || user?.email}
                        rules={[{ required: true, message: 'Required' }]}
                      >
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="date_of_inspection" label="Date of Inspection" initialValue={dayjs()} rules={[{ required: true, message: 'Required' }]}>
                        <DatePicker style={{ width: '100%' }} size="large" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item
                        name="qty"
                        label="Qty"
                        rules={[
                          { required: true, message: 'Required' },
                          {
                            type: 'number',
                            min: 1,
                            max: linePlannedQty,
                            message: `Between 1 and ${linePlannedQty}`,
                          },
                        ]}
                        extra={`Number of measurement columns is capped at planned quantity (${linePlannedQty}). Save report information after changing Qty.`}
                      >
                        <InputNumber min={1} max={linePlannedQty} style={{ width: '100%', minHeight: TOUCH_MIN_HEIGHT }} size="large" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="qc_approved_by" label="Q.C. Approved By">
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="qc_approval_date" label="Q.C. Approval Date">
                        <DatePicker style={{ width: '100%' }} size="large" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="po_line_no" label="PO Line No.">
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="client_po_no" label="Client PO No.">
                        <Input size="large" style={{ minHeight: TOUCH_MIN_HEIGHT }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item name="instruments_used" label="Instruments Used">
                        <Select
                          mode="multiple"
                          placeholder="Select equipment"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          size="large"
                          loading={loadingEquipmentOptions}
                          options={equipmentOptions}
                          style={{ minHeight: TOUCH_MIN_HEIGHT }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item name="remarks" label="Remarks">
                        <TextArea rows={2} style={{ fontSize: 16 }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Card>

              {!stageId ? (
                <Alert
                  message="Select a Stage to load stage-specific details and dimension measurements."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              ) : (
                <Card className="dimension-measurements-card" bodyStyle={{ padding: 0 }}>
                  <div className="dimension-measurements-card__header">
                    <div className="dimension-measurements-card__title">Dimension Measurements</div>
                    <Space wrap size="middle" className="dimension-measurements-card__actions" />
                  </div>
                  {loadingDimensions ? (
                    <div className="dimension-measurements-card__body">
                      <Spin tip="Loading dimensions..." />
                    </div>
                  ) : (
                    <div className="dimension-measurements-card__body">
                      <EngineeringSymbolPalette disabled={!canEdit} onInsert={insertEngineeringSymbol} />
                      <div className="dimension-measurements__table-wrap">
                        <Table
                          rowKey="key"
                          columns={tableColumns}
                          dataSource={dimensionRows}
                          pagination={false}
                          scroll={{ x: 'max-content' }}
                          size="middle"
                          className="dimension-measurements__table"
                        />
                      </div>
                      <Typography.Paragraph type="secondary" className="dimension-measurements__tip">
                        Tip: The number of Actual columns automatically adjusts based on the Qty value above. Each column represents one piece being inspected.
                      </Typography.Paragraph>
                      {canEdit && (
                        <div style={{ marginTop: 18 }}>
                          <Button
                            type="primary"
                            size="middle"
                            icon={<PlusOutlined />}
                            onClick={handleAddRow}
                            style={{
                              minWidth: 124,
                              borderRadius: 10,
                            }}
                          >
                            Add Row
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              )}

              <div
                style={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 5,
                  marginTop: 24,
                  padding: '14px 20px',
                  background: 'rgba(255,255,255,0.96)',
                  borderTop: '1px solid #dbe6f2',
                  boxShadow: '0 -6px 18px rgba(15, 23, 42, 0.06)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                  <Link to="/qms/dimensional-inspection">
                    <Button size="middle" style={{ minWidth: 118, borderRadius: 10 }}>
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="primary"
                    size="middle"
                    onClick={() => void handleSaveAll()}
                    loading={
                      createInspectionMutation.isPending ||
                      startInspectionMutation.isPending ||
                      updateReportMetadataMutation.isPending ||
                      recordMeasurementsMutation.isPending ||
                      addDimensionMutation.isPending
                    }
                    style={{ minWidth: 144, borderRadius: 10 }}
                  >
                    Save Report
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}
      </>
      )}

    </div>
  );
};

export default DimensionalInspectionPage;
